import { useContext, useEffect } from 'react';
import { VulnDrawerTabsContext } from '../NRVulnerabilitiesTable/NRVulnerabilityDetails/NRVulnerabilityDetails';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import NRTitleWithRightTotal from '../NRRightTotal/NRRightTotal';
import NRNoData from './NRNoData/NRNoData';
import NRVirtualizedScroll from '../NRVulnerabilitiesTable/NRVulnerabilityDetails/NRKnownExploits/KnownExploitInfo/KnownExploitInfoCard';
import NRTag from '../NRTag/NRTag';
import { capitalizeFirstLetter } from '../../utils/utils';
import { CONSTANTS } from '../../constants/constants';
import NRValueTooltipIcon from '../NRReactGridCustomComponents/NRCustomProviders/NRValueTooltipIcon';
import { PACKAGE_MANAGER_ICONS } from '../NRReactGridCustomComponents/NRCustomProviders/NRSBOMTypeProvider';
import { useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import NRLoader from '../NRLoader/NRLoader';
import Typography from '@mui/material/Typography';
const { BADGES } = CONSTANTS.TABLE.SBOM_TABLE.SBOM_DETAIL_DRAWER;

function NRDependencies({
  threshold,
  scrollTarget,
  index,
  scrollableId,
  context,
  dependencies,
  title,
  itemSize,
  showDivider = true,
  handleNameClick,
  isLoading
}) {
  const theme = useTheme();
  const { setScrollSpyTabsValue } = useContext(context || VulnDrawerTabsContext);

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold,
    target: !!scrollTarget ? scrollTarget : undefined
  });

  useEffect(() => {
    setScrollSpyTabsValue(index, trigger);
  }, [trigger]);

  function NRDependency({ index, style }) {
    const dependency = dependencies[index];
    const isDependency = dependency.direct !== undefined;
    const itemHeight = isDependency ? 64 : 40;
    const isDarkMode = theme.palette.mode === 'dark';
    const packageType = dependency.component?.package?.type;
    const iconDefinition = (!!packageType && PACKAGE_MANAGER_ICONS[packageType?.toUpperCase()]) || PACKAGE_MANAGER_ICONS.DEFAULT;
    const packageIcon = isDarkMode ? iconDefinition?.ICON_DARK || iconDefinition?.ICON : iconDefinition?.ICON;
    const tooltip = iconDefinition?.TOOLTIP;

    return (
      <Box sx={{ py: 1 }} style={{ ...style, height: itemHeight }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap',
            gap: 2,
            height: 24
          }}
        >
          <Box display="flex" alignItems="center" sx={{ flexShrink: 0 }}>
            {handleNameClick ? (
              <Button
                type="button"
                variant="text"
                aria-label={`Filter by ${dependency.component.name}`}
                onClick={() => handleNameClick(dependency)}
                sx={{
                  ...theme.typography.body2,
                  textTransform: 'none',
                  minWidth: 0,
                  padding: 0,
                  paddingRight: theme.spacing(0.5),
                  lineHeight: theme.spacing(3),
                  justifyContent: 'flex-start',
                  color: theme.palette.blue.link,
                  textDecoration: 'none',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'underline'
                  }
                }}
              >
                {dependency.component.name}
              </Button>
            ) : (
              <Typography
                component="span"
                variant="body2"
                sx={{
                  paddingRight: theme.spacing(0.5),
                  lineHeight: theme.spacing(3)
                }}
              >
                {dependency.component.name}
              </Typography>
            )}
            <Typography
              tabIndex={0}
              component={'span'}
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                maxWidth: 470,
                paddingRight: theme.spacing(1),
                lineHeight: theme.spacing(3)
              }}
            >
              {dependency.component.version?.id}
            </Typography>
            {!!dependency.component?.package?.type && (
              <NRValueTooltipIcon
                tabIndex={0}
                icon={packageIcon}
                tooltip={tooltip}
                tooltipComponent={
                  <span>
                    Package Match: <b>{tooltip}</b>
                  </span>
                }
              />
            )}
          </Box>
          {!!dependency?.badges?.length && (
            <Box display="flex" flexWrap="nowrap" gap={2} alignItems="center">
              {dependency?.badges?.map(badgeKey => (
                <NRTag
                  key={badgeKey}
                  text={BADGES[badgeKey].LABEL}
                  tooltipContent={<span style={{ whiteSpace: 'pre-line' }}>{BADGES[badgeKey].TOOLTIP}</span>}
                />
              ))}
            </Box>
          )}
          {!!dependency?.component?.scope?.length && (
            <Box display="flex" flexWrap="nowrap" gap={2} alignItems="center" sx={{ flexShrink: 0 }}>
              {dependency?.component?.scope?.map((s, i) => (
                <NRTag key={i} text={capitalizeFirstLetter(s.value)} tooltipContent={s.description} isScope={true} actualScope={s.actualScope} />
              ))}
            </Box>
          )}
        </Box>
        {isDependency && (
          <Box sx={{ whiteSpace: 'nowrap', width: '100%', display: 'flex', alignItems: 'center', height: 20, mt: 0.5 }}>
            <Typography
              tabIndex={0}
              component={'span'}
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                maxWidth: 470,
                paddingRight: theme.spacing(1)
              }}
            >
              {dependency.direct ? 'Direct' : 'Indirect'}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box id={scrollableId} mt={theme.spacing(3)}>
      <NRTitleWithRightTotal totalCount={dependencies?.length || 0} title={title} />
      {isLoading ? (
        <Box sx={{ height: '60px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <NRLoader />
        </Box>
      ) : (
        <>
          {!!dependencies?.length && <NRVirtualizedScroll list={dependencies} Component={NRDependency} itemHeight={itemSize} />}
          {!dependencies?.length && <NRNoData noDataTitle={title} />}
        </>
      )}
      {showDivider && <Divider sx={{ pt: theme.spacing(2) }} />}
    </Box>
  );
}

export default NRDependencies;
